"use client";

import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { ChevronLeft, ChevronRight, RotateCcw, X, ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Image } from "@/lib/client/components/Image";
import type { MediaDisplay } from "@/lib/shared/types/interfaces";
import { cn, Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle } from "@/ui";

interface MediaLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  items: MediaDisplay[];
  initialIndex: number;
  videoIndex?: number; // Index of the video item, if present
}

/** Gallery chrome on dark media — light icons stay light; surface brightens on hover. */
const lightboxControlClass =
  "inline-flex size-11 shrink-0 items-center justify-center rounded-full border border-white/20 bg-black/45 text-white shadow-sm backdrop-blur-sm transition-colors duration-200 outline-none hover:border-white/45 hover:bg-white/20 focus-visible:border-white/55 focus-visible:ring-[3px] focus-visible:ring-white/45 active:bg-white/25 disabled:pointer-events-none disabled:border-white/10 disabled:bg-black/30 disabled:text-white/40 [&_svg]:pointer-events-none [&_svg]:shrink-0";

export const MediaLightbox = (props: MediaLightboxProps) => {
  const { isOpen, onClose, items, initialIndex, videoIndex } = props;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  // Reset state when opened or index changes
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen, initialIndex]);

  // Reset zoom when changing items
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
  }, [items.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
  }, [items.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        goToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goToNext, goToPrevious, onClose]);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.5, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.5, 0.5);
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale((prev) => {
      const newScale = Math.max(0.5, Math.min(3, prev + delta));
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale > 1) {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      }
    },
    [scale, position],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && scale > 1) {
        e.preventDefault();
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, scale, dragStart],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (scale > 1) {
      handleReset();
    } else {
      setScale(2);
    }
  }, [scale, handleReset]);

  const currentItem = items[currentIndex];
  // Determine if current item is a video
  const isVideo = videoIndex !== undefined && currentIndex === videoIndex;

  if (!currentItem) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogPortal>
        <DialogOverlay className="bg-black/95" />
        <DialogContent
          className="!inset-0 !h-dvh !w-screen !max-h-none !max-w-none !translate-x-0 !translate-y-0 overflow-hidden rounded-none border-0 bg-transparent p-0 shadow-none"
          showCloseButton={false}
          onPointerDownOutside={onClose}
        >
          <VisuallyHidden.Root>
            <DialogTitle>{currentItem.alt || "Media preview"}</DialogTitle>
          </VisuallyHidden.Root>
          {/* Main content area */}
          <div className="relative flex h-full w-full min-w-0 flex-col items-center justify-center">
            {/* Top toolbar */}
            <div className="absolute top-0 right-0 left-0 z-50 flex items-center justify-between p-4">
              <div className="rounded-full border border-white/20 bg-black/45 px-3.5 py-2 text-white shadow-sm backdrop-blur-sm">
                <span className="text-sm tracking-wide">
                  {currentIndex + 1} / {items.length}
                </span>
              </div>
              <button
                type="button"
                className={lightboxControlClass}
                onClick={onClose}
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </button>
            </div>

            {/* Zoom controls - only show for images */}
            {!isVideo && (
              <div className="absolute top-20 right-4 z-50 flex flex-col gap-2.5">
                <button
                  type="button"
                  className={lightboxControlClass}
                  onClick={handleZoomIn}
                  disabled={scale >= 3}
                >
                  <ZoomIn className="h-5 w-5" />
                  <span className="sr-only">Zoom in</span>
                </button>
                <button
                  type="button"
                  className={lightboxControlClass}
                  onClick={handleZoomOut}
                  disabled={scale <= 0.5}
                >
                  <ZoomOut className="h-5 w-5" />
                  <span className="sr-only">Zoom out</span>
                </button>
                <button
                  type="button"
                  className={lightboxControlClass}
                  onClick={handleReset}
                  disabled={scale === 1 && position.x === 0 && position.y === 0}
                >
                  <RotateCcw className="h-5 w-5" />
                  <span className="sr-only">Reset</span>
                </button>
              </div>
            )}

            {/* Navigation buttons */}
            {items.length > 1 && (
              <>
                <button
                  type="button"
                  className={cn(
                    lightboxControlClass,
                    "absolute top-1/2 left-4 z-50 -translate-y-1/2",
                  )}
                  onClick={goToPrevious}
                >
                  <ChevronLeft className="h-6 w-6" />
                  <span className="sr-only">Previous</span>
                </button>
                <button
                  type="button"
                  className={cn(
                    lightboxControlClass,
                    "absolute top-1/2 right-4 z-50 -translate-y-1/2",
                  )}
                  onClick={goToNext}
                >
                  <ChevronRight className="h-6 w-6" />
                  <span className="sr-only">Next</span>
                </button>
              </>
            )}

            {/* Media display area */}
            <div
              ref={containerRef}
              className="flex min-h-0 w-full flex-1 items-stretch justify-center overflow-hidden p-4"
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {isVideo ? (
                <video
                  src={currentItem.src}
                  controls
                  playsInline
                  className="h-full w-full rounded-lg object-contain"
                />
              ) : (
                <div
                  ref={imageRef}
                  className={cn(
                    "relative h-full w-full transition-transform duration-200 ease-out",
                    isDragging ? "cursor-grabbing" : scale > 1 ? "cursor-grab" : "cursor-zoom-in",
                  )}
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  }}
                  onDoubleClick={handleDoubleClick}
                >
                  <Image
                    src={currentItem.src}
                    alt={currentItem.alt || ""}
                    fill
                    className="object-contain"
                    sizes="100vw"
                  />
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {items.length > 1 && (
              <div className="z-50 flex w-full items-center gap-2 overflow-x-auto bg-black/50 p-4 [justify-content:safe_center]">
                {items.map((item, index) => (
                  <button
                    key={item.id || `thumb-${index}`}
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      "relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border-2 transition-all",
                      currentIndex === index
                        ? "border-white"
                        : "border-transparent hover:border-white/50",
                    )}
                  >
                    {videoIndex !== undefined && index === videoIndex ? (
                      <div className="relative h-full w-full">
                        <Image
                          src={item.thumbnailSrc || item.src}
                          alt={item.alt || "Video"}
                          fill
                          className="object-cover"
                          sizes="64px"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <svg
                            className="h-4 w-4 text-white"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    ) : (
                      <Image
                        src={item.thumbnailSrc || item.src}
                        alt={item.alt || ""}
                        fill
                        className="object-cover"
                        sizes="64px"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export default MediaLightbox;
